/**
 * Billing review > Billing IDs: the numbers a billing system needs. LEDES
 * wants a matter number on every case and an ID and classification for every
 * timekeeper. Each field saves when it loses focus.
 */
import { AlertTriangle } from "lucide-react";
import { useEffect, useRef, type FC } from "react";

import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { reviewActions } from "../actions";
import { CaseDot } from "../components/CasePicker";
import { useMatters, useTimekeepers } from "../queries";
import { Segmented } from "../settings/SettingRow";
import type { MatterRow, TimeMatter, Timekeeper } from "../types";
import { formatHours } from "../utils";
import { CLASSIFICATIONS } from "./reviewModel";

type CellInputProps = {
  label: string;
  value?: string | number | null;
  placeholder?: string;
  required?: boolean;
  mono?: boolean;
  prefix?: string;
  onSave: (value: string) => void;
};

/** Uncontrolled so typing never fights a refetch; re-keyed when the saved value changes. */
export const CellInput: FC<CellInputProps> = ({
  label,
  value,
  placeholder,
  required,
  mono,
  prefix,
  onSave
}) => {
  const saved = value === null || value === undefined ? "" : String(value);
  const sent = useRef(saved);
  useEffect(() => {
    sent.current = saved;
  }, [saved]);
  return (
    <span className="relative flex items-center">
      {prefix ? (
        <span className="pointer-events-none absolute left-2 text-sm text-fg-3">
          {prefix}
        </span>
      ) : null}
      <Input
        key={saved}
        aria-label={label}
        aria-invalid={required && !saved ? true : undefined}
        defaultValue={saved}
        placeholder={placeholder}
        className={cn(
          "h-7 text-sm",
          mono && "font-mono",
          prefix && "pl-5",
          required && !saved && "border-warn-line bg-warn-soft"
        )}
        onBlur={(e) => {
          const next = e.currentTarget.value.trim();
          if (next === sent.current) return;
          sent.current = next;
          onSave(next);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
    </span>
  );
};

const saveMatter = (row: MatterRow, patch: Partial<TimeMatter>) =>
  void reviewActions.saveCaseBilling(row.individual_id, patch);

const saveKeeper = (k: Timekeeper, patch: Partial<Timekeeper>) =>
  void reviewActions.saveTimekeeper(k.user_id || k.uid, patch);

const money = (text: string): number | null => {
  const n = Number(text.replace(/[^0-9.]/g, ""));
  return text.trim() && Number.isFinite(n) ? n : null;
};

const CasesTable: FC<{ rows: MatterRow[] }> = ({ rows }) => (
  <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
    <Table className="table-fixed text-base">
      <colgroup>
        <col />
        <col className="w-28" />
        <col className="w-28" />
        <col className="w-36" />
        <col className="w-36" />
        <col className="w-28" />
        <col className="w-20" />
      </colgroup>
      <TableHeader className="bg-fill-1">
        <TableRow>
          <TableHead className="h-8 text-sm">Case</TableHead>
          <TableHead className="h-8 text-sm">Client no.</TableHead>
          <TableHead className="h-8 text-sm">Matter no.</TableHead>
          <TableHead
            className="h-8 text-sm"
            title="The matter ID the company assigns. Sent as CLIENT_MATTER_ID."
          >
            Client&apos;s matter ID
          </TableHead>
          <TableHead className="h-8 text-sm">Fee arrangement</TableHead>
          <TableHead className="h-8 text-right text-sm">Flat fee</TableHead>
          <TableHead className="h-8 text-right text-sm">This week</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((m) => {
          const flat = m.fee_arrangement === "flat_fee";
          return (
            <TableRow key={m.individual_id} data-testid="matter-row">
              <TableCell className="py-1.5">
                <span className="flex min-w-0 items-center gap-2">
                  {!m.matter_no ? (
                    <AlertTriangle
                      className="size-3.5 shrink-0 text-warn"
                      aria-label="Missing matter number"
                    />
                  ) : null}
                  <CaseDot id={m.individual_id} />
                  <span className="truncate">{m.label}</span>
                </span>
              </TableCell>
              <TableCell className="py-1.5">
                <CellInput
                  label={`Client number for ${m.label}`}
                  value={m.client_no}
                  mono
                  onSave={(v) => saveMatter(m, { client_no: v || null })}
                />
              </TableCell>
              <TableCell className="py-1.5">
                <CellInput
                  label={`Matter number for ${m.label}`}
                  value={m.matter_no}
                  placeholder="Required"
                  required
                  mono
                  onSave={(v) => saveMatter(m, { matter_no: v || null })}
                />
              </TableCell>
              <TableCell className="py-1.5">
                {m.client_matter_id_required || m.client_matter_id ? (
                  <CellInput
                    label={`Client's matter ID for ${m.label}`}
                    value={m.client_matter_id}
                    placeholder="Company's ID"
                    required={m.client_matter_id_required}
                    mono
                    onSave={(v) =>
                      saveMatter(m, { client_matter_id: v || null })
                    }
                  />
                ) : (
                  <span className="text-sm text-fg-3">Not used</span>
                )}
              </TableCell>
              <TableCell className="py-1.5">
                <Segmented
                  label={`Fee arrangement for ${m.label}`}
                  value={flat ? "flat_fee" : "hourly"}
                  options={[
                    { id: "hourly", label: "Hourly" },
                    { id: "flat_fee", label: "Flat" }
                  ]}
                  onChange={(v) => saveMatter(m, { fee_arrangement: v })}
                />
              </TableCell>
              <TableCell className="py-1.5 text-right">
                {flat ? (
                  <CellInput
                    label={`Flat fee for ${m.label}`}
                    value={m.flat_fee}
                    prefix="$"
                    onSave={(v) => saveMatter(m, { flat_fee: money(v) })}
                  />
                ) : (
                  <span className="text-fg-3">–</span>
                )}
              </TableCell>
              <TableCell className="py-1.5 text-right tabular-nums">
                {formatHours(m.week_hours)}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

const KeepersTable: FC<{ rows: Timekeeper[]; defaultRate: number }> = ({
  rows,
  defaultRate
}) => (
  <div className="shrink-0 overflow-hidden rounded-xl border border-line-2">
    <Table className="table-fixed text-base">
      <colgroup>
        <col />
        <col className="w-32" />
        <col className="w-44" />
        <col className="w-28" />
      </colgroup>
      <TableHeader className="bg-fill-1">
        <TableRow>
          <TableHead className="h-8 text-sm">Person</TableHead>
          <TableHead className="h-8 text-sm">Timekeeper ID</TableHead>
          <TableHead className="h-8 text-sm">Classification</TableHead>
          <TableHead className="h-8 text-right text-sm">Rate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((k) => {
          const name = k.name || k.email || k.user_id;
          const missing = !k.timekeeper_id || !k.classification;
          return (
            <TableRow key={k.user_id || k.uid} data-testid="timekeeper-row">
              <TableCell className="py-1.5">
                <span className="flex min-w-0 items-center gap-2">
                  {missing ? (
                    <AlertTriangle
                      className="size-3.5 shrink-0 text-warn"
                      aria-label="Missing timekeeper ID or classification"
                    />
                  ) : null}
                  <span className="truncate font-medium">{name}</span>
                  {k.role ? (
                    <span className="truncate text-fg-3">{k.role}</span>
                  ) : null}
                </span>
              </TableCell>
              <TableCell className="py-1.5">
                <CellInput
                  label={`Timekeeper ID for ${name}`}
                  value={k.timekeeper_id}
                  placeholder="Required"
                  required
                  mono
                  onSave={(v) => saveKeeper(k, { timekeeper_id: v || null })}
                />
              </TableCell>
              <TableCell className="py-1.5">
                <Select
                  value={k.classification || undefined}
                  onValueChange={(v) =>
                    saveKeeper(k, {
                      classification: v as Timekeeper["classification"]
                    })
                  }
                >
                  <SelectTrigger
                    size="sm"
                    aria-label={`Classification for ${name}`}
                    className={cn(
                      "h-7 w-full text-sm",
                      !k.classification && "border-warn-line bg-warn-soft"
                    )}
                  >
                    <SelectValue placeholder="Required" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="py-1.5">
                <CellInput
                  label={`Rate for ${name}`}
                  value={k.rate}
                  placeholder={String(defaultRate)}
                  prefix="$"
                  onSave={(v) => saveKeeper(k, { rate: money(v) })}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

export const BillingIdsTab: FC = () => {
  const matters = useMatters();
  const keepers = useTimekeepers();
  const rows = [...(matters.data ?? [])].sort(
    (a, b) =>
      Number(Boolean(a.matter_no)) - Number(Boolean(b.matter_no)) ||
      a.label.localeCompare(b.label)
  );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-4"
      data-testid="billing-ids"
    >
      <p className="max-w-3xl text-base leading-4.75 text-fg-3">
        These are the IDs your billing system uses. LEDES files need a matter
        number on every case and a timekeeper ID and classification for every
        person. Companies on e-billing can also require their own matter ID for
        each employee&apos;s case.
      </p>

      <div className="flex flex-col gap-2">
        <span className="text-base font-medium">Cases</span>
        {matters.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : rows.length ? (
          <CasesTable rows={rows} />
        ) : (
          <p className="text-sm text-fg-3">
            Cases appear here once they have time or billing details.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-base font-medium">Timekeepers</span>
        {keepers.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <KeepersTable
            rows={keepers.data?.timekeepers ?? []}
            defaultRate={keepers.data?.default_rate ?? 0}
          />
        )}
      </div>
    </div>
  );
};
